import { StatusField } from '@amfintech/react-admin-ui';
import { createHelpers, createResource } from '@amfintech/refine-react-admin';
import dayjs from 'dayjs';

import { TomswinSync, TomswinSyncStatus } from '~/api';

import { resourceNames } from './resource-names';

const { defineFields } = createHelpers<TomswinSync>({
  resourceName: resourceNames.tomswinSync,
});

const formatStatus = (status: TomswinSyncStatus) => {
  switch (status) {
    case TomswinSyncStatus.Completed:
      return 'success';
    case TomswinSyncStatus.Failed:
      return 'danger';
    default:
      return 'pending';
  }
};

enum TOMSWIN_SYNC_FILE_TYPES {
  FMD_FUND = 'FMD_FUND',
  FMD_MGT = 'FMD_MGT',
  FMD_FUND_POL = 'FMD_FUND_POL',
  FMD_PF = 'FMD_PF',
  FMD_NAV = 'FMD_NAV',
  FMD_CAL = 'FMD_CAL',
  FMD_CALF = 'FMD_CALF',
  FMD_CIF = 'FMD_CIF',
  FMD_TRUSTEE = 'FMD_TRUSTEE',
  FMD_MACC = 'FMD_MACC',
  FMD_INVBNK = 'FMD_INVBNK',
  FMD_UTACC = 'FMD_UTACC',
  FMD_TRX_HIST = 'FMD_TRX_HIST',
  FMD_TXN_STATUS = 'FMD_TXN_STATUS',
  FMD_BANK = 'FMD_BANK',
  FMD_ADR = 'FMD_ADR',
  FundFullList = 'FundFullList',
  FundDocumentList = 'FundDocumentList',
  FundUpdateList = 'FundUpdateList',
  CollectionBankAccounts = 'CollectionBankAccounts',
}

export const tomswinSyncResource = createResource({
  name: resourceNames.tomswinSync,
  label: 'Tomswin Sync',
  fields: defineFields([
    'id',
    'batchId',
    'fileName',
    'fileType',
    'data',
    'failureReason',
    'status',
    'lineNo',
    'fileLastModifiedAt',
    'createdAt',
    'updatedAt',
  ]),
  allowDownload: false,
  allowCreate: false,
  allowDelete: false,
  allowEdit: false,
  list: {
    syncWithLocation: true,
    filters: {
      required: true,
      collapsible: 'expand-by-default',
    },
  },
  defaultValues: {},
  defaultSorter: [
    {
      field: 'createdAt',
      order: 'desc',
    },
  ],
  defaultPageSize: 200,
  timeSensitive: {
    pollingInterval: 10000,
    systemNotification: true,
  },
  filterControls: {
    createdAt: {
      type: 'daterange',
      config: {
        label: 'Indexed date',
      },
    },
    batchId: {
      type: 'text',
      config: {
        label: 'Batch ID',
      },
      operator: 'eq',
    },
    fileType: {
      type: 'select',
      config: {
        label: 'File type',
        options: Object.values(TOMSWIN_SYNC_FILE_TYPES).map((type) => ({
          label: type,
          value: type,
        })),
      },
      operator: 'eq',
    },
    status: {
      type: 'select',
      config: {
        label: 'Status',
        options: [
          { label: 'Completed', value: 'Completed' },
          { label: 'Failed', value: 'Failed' },
          { label: 'Pending', value: 'Pending' },
        ],
      },
      operator: 'eq',
    },
  },
  columns: ({ LinkToDetails }) => [
    {
      id: 'createdAt',
      header: 'Indexed time',
      accessorKey: 'createdAt',
      cell: (data) => {
        const createdAt = data.cell.getValue<string>();

        return (
          <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
            {dayjs(createdAt).format('DD MMM YYYY HH:mm:ss')}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'fileLastModifiedAt',
      header: 'File Modified At',
      accessorKey: 'fileLastModifiedAt',
      cell: (data) => {
        const fileLastModifiedAt = data.cell.getValue<string>();

        return <>{dayjs(fileLastModifiedAt).format('DD MMM YYYY HH:mm:ss')}</>;
      },
    },
    {
      id: 'updatedAt',
      header: 'Processed At',
      accessorKey: 'updatedAt',
      cell: (data) => {
        const updatedAt = data.cell.getValue<string>();

        return <>{dayjs(updatedAt).format('DD MMM YYYY HH:mm:ss')}</>;
      },
    },
    {
      id: 'batchId',
      header: 'Batch ID',
      accessorKey: 'batchId',
      cell: (data) => {
        const batchId = data.cell.getValue<string>();

        return <>{batchId}</>;
      },
    },
    {
      id: 'fileName',
      header: 'File Name',
      accessorKey: 'fileName',
      cell: (data) => {
        const fileName = data.cell.getValue<string>();

        return <>{fileName}</>;
      },
    },
    {
      id: 'fileType',
      header: 'File Type',
      accessorKey: 'fileType',
      cell: (data) => {
        const fileType = data.cell.getValue<string>();

        return <>{fileType}</>;
      },
    },
    {
      id: 'lineNo',
      header: 'Line No',
      accessorKey: 'lineNo',
      cell: (data) => {
        const lineNo = data.cell.getValue<number>();

        return <>{lineNo}</>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (data) => {
        const status = data.cell.getValue<TomswinSyncStatus>();

        // If status is failed, show the status and failure reason
        if (status === TomswinSyncStatus.Failed) {
          const failureReason = data.row.original.failureReason;

          return (
            <>
              <StatusField status={formatStatus(status)} label={status} />
              <p className="text-sm text-gray-500">{failureReason}</p>
            </>
          );
        }

        return <StatusField status={formatStatus(status)} label={status} />;
      },
    },
  ],
});
